import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import CTASection from '../components/CTASection';
import { CheckCircle2, TrendingUp, Zap, Users, Shield, Headphones, ArrowRight, Star } from 'lucide-react';

const Home = () => {
  const benefits = [
    {
      icon: <Zap className="w-8 h-8 text-blue-700" />,
      title: 'Snelle Implementatie',
      description: 'Van plan tot live in recordtijd. Wij zorgen voor een vlotte en professionele implementatie.',
    },
    {
      icon: <Users className="w-8 h-8 text-blue-700" />,
      title: 'Volledig Maatwerk',
      description: 'Elk bedrijf is uniek. Wij passen het CRM systeem perfect aan op uw werkprocessen.',
    },
    {
      icon: <Shield className="w-8 h-8 text-blue-700" />,
      title: 'Bewezen Expertise',
      description: 'Jarenlange ervaring met complexe CRM implementaties voor bedrijven van alle groottes.',
    },
    {
      icon: <Headphones className="w-8 h-8 text-blue-700" />,
      title: 'Continue Ondersteuning',
      description: 'Ook na de implementatie staan wij voor u klaar met support en optimalisaties.',
    },
  ];

  const services = [
    {
      title: 'CRM Implementatie',
      description: 'Complete implementatie van Zoho CRM afgestemd op uw bedrijfsprocessen en doelstellingen.',
      link: '/diensten',
    },
    {
      title: 'Automatisering',
      description: 'Bespaar tijd en verhoog efficiency door workflows en processen te automatiseren.',
      link: '/diensten',
    },
    {
      title: 'Integraties',
      description: 'Koppel uw CRM aan boekhouding, marketing tools en andere systemen voor één waarheid.',
      link: '/diensten',
    },
    {
      title: 'Consultancy',
      description: 'Strategisch advies voor optimaal gebruik van uw CRM en het verbeteren van processen.',
      link: '/diensten',
    },
  ];

  const testimonials = [
    {
      name: 'Jan Vermeulen',
      company: 'TechStart BV',
      text: 'Guijs Consultancy heeft ons geholpen om onze salesprocessen volledig te digitaliseren. De ROI was binnen 6 maanden zichtbaar.',
      rating: 5,
    },
    {
      name: 'Linda de Vries',
      company: 'Growth Solutions',
      text: 'Professionele aanpak en uitstekende kennis van Zoho. Het team denkt echt mee en levert kwaliteit.',
      rating: 5,
    },
    {
      name: 'Peter Jansen',
      company: 'InnovateNL',
      text: 'Van begin tot eind perfect begeleid. De implementatie verliep soepel en het resultaat overtreft onze verwachtingen.',
      rating: 5,
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative pt-20 pb-32 overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMGNvbnN1bHRpbmd8ZW58MHx8fHwxNzczNzQ2NTMwfDA&ixlib=rb-4.1.0&q=85"
            alt="Professional Consulting"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-blue-900/95 via-blue-800/90 to-blue-900/95"></div>
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-4xl">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              CRM systemen die jouw bedrijf laten groeien
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 mb-8 leading-relaxed">
              Specialist in Zoho CRM implementaties en automatisering
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 mb-12">
              <Link to="/contact">
                <Button 
                  size="lg" 
                  className="bg-white text-blue-700 hover:bg-gray-100 px-8 py-6 text-lg font-semibold rounded-lg shadow-xl hover:shadow-2xl transition-all duration-300 group"
                >
                  Plan een Demo
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
                </Button>
              </Link>
              
              <a href="tel:0638823471">
                <Button 
                  size="lg" 
                  variant="outline"
                  className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-blue-700 px-8 py-6 text-lg font-semibold rounded-lg transition-all duration-300"
                >
                  Bel Direct: 06 3882 3471
                </Button>
              </a>
            </div>

            {/* USPs */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="flex items-center space-x-3 text-white">
                <CheckCircle2 className="w-6 h-6 text-green-400 flex-shrink-0" />
                <span className="font-medium">Snelle implementatie</span>
              </div>
              <div className="flex items-center space-x-3 text-white">
                <CheckCircle2 className="w-6 h-6 text-green-400 flex-shrink-0" />
                <span className="font-medium">Volledig maatwerk</span>
              </div>
              <div className="flex items-center space-x-3 text-white">
                <CheckCircle2 className="w-6 h-6 text-green-400 flex-shrink-0" />
                <span className="font-medium">Ervaring met complexe processen</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Waarom kiezen voor Guijs Consultancy?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Wij combineren technische expertise met zakelijk inzicht om uw CRM implementatie tot een succes te maken.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <Card key={`item-${index}`} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <CardContent className="p-6">
                  <div className="mb-4">{benefit.icon}</div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Onze Diensten
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Van implementatie tot optimalisatie - wij begeleiden u in elke fase van uw CRM journey.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <Card key={`item-${index}`} className="border-2 border-gray-200 hover:border-blue-500 transition-all duration-300 hover:shadow-lg">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">{service.title}</h3>
                  <p className="text-gray-600 mb-4">{service.description}</p>
                  <Link 
                    to={service.link}
                    className="text-blue-700 hover:text-blue-800 font-semibold inline-flex items-center group"
                  >
                    Meer informatie
                    <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Wat Onze Klanten Zeggen
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Ontdek hoe wij bedrijven helpen groeien met de juiste CRM oplossingen.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <Card key={`item-${index}`} className="border-0 shadow-lg">
                <CardContent className="p-6">
                  <div className="flex mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                    ))}
                  </div>
                  <p className="text-gray-700 mb-4 italic">"{testimonial.text}"</p>
                  <div className="border-t pt-4">
                    <p className="font-semibold text-gray-900">{testimonial.name}</p>
                    <p className="text-sm text-gray-600">{testimonial.company}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CTASection />
    </div>
  );
};

export default Home;
